import { useEffect, useState } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { 
  Shield, 
  Users, 
  Building2, 
  Calendar, 
  DoorOpen,
  TrendingUp,
  ArrowLeft,
  Trash2,
  Edit,
  Eye,
  ChevronDown,
  ChevronUp,
  X,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Search,
  Key,
  CreditCard
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { formatLocalDate } from '../lib/utils'
import { api, type AdminStats, type AdminTenant, type AdminTenantDetail, type AdminUser } from '../lib/api'

export function AdminPage() {
  const navigate = useNavigate()
  const { user, isAuthenticated, isLoading, checkAuth } = useAuthStore()
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [tenants, setTenants] = useState<AdminTenant[]>([])
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'tenants' | 'users'>('overview')
  const [peopleSearch, setPeopleSearch] = useState('')
  
  // Tenant detail modal
  const [selectedTenant, setSelectedTenant] = useState<AdminTenantDetail | null>(null)
  const [tenantModalOpen, setTenantModalOpen] = useState(false)
  const [tenantLoading, setTenantLoading] = useState(false)
  
  // Edit tenant modal
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editTenantData, setEditTenantData] = useState<{ name: string; slug: string } | null>(null)
  const [editingSaving, setEditingSaving] = useState(false)
  
  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [tenantToDelete, setTenantToDelete] = useState<AdminTenant | null>(null)
  const [deleteTypedName, setDeleteTypedName] = useState('')
  const [deleting, setDeleting] = useState(false)
  
  // Bookings chart data
  const [bookingsByDay, setBookingsByDay] = useState<{ date: string; count: number }[]>([])
  
  // Expanded rows in tenant table
  const [expandedTenants, setExpandedTenants] = useState<Set<string>>(new Set())
  
  // User management state
  const [editUserModalOpen, setEditUserModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [editUserData, setEditUserData] = useState<{ name: string; email: string }>({ name: '', email: '' })
  const [editUserSaving, setEditUserSaving] = useState(false)
  const [deleteUserConfirmOpen, setDeleteUserConfirmOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null)
  const [deleteUserTypedName, setDeleteUserTypedName] = useState('')
  const [deletingUser, setDeletingUser] = useState(false)
  const [resetPasswordLoading, setResetPasswordLoading] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    checkAuth()
  }, [])
  
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [isLoading, isAuthenticated, navigate])
  
  useEffect(() => {
    if (isAuthenticated && user && !user.isGlobalAdmin) {
      navigate({ to: '/dashboard' })
    }
  }, [isAuthenticated, user, navigate])
  
  useEffect(() => {
    if (isAuthenticated && user?.isGlobalAdmin) {
      loadData()
    }
  }, [isAuthenticated, user])
  
  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [statsRes, tenantsRes, usersRes] = await Promise.all([
        api.admin.getStats(),
        api.admin.listTenants(),
        api.admin.listUsers(),
      ])
      setStats(statsRes.stats)
      setTenants(tenantsRes.tenants)
      setUsers(usersRes.users)
      
      // Load last 30 days of booking data
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 30)
      const bookingRes = await api.admin.getBookingStats(
        formatLocalDate(startDate),
        formatLocalDate(endDate)
      )
      setBookingsByDay(bookingRes.bookingsByDay)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin data')
    } finally {
      setLoading(false)
    }
  }
  
  const handleViewTenant = async (tenantId: string) => {
    setTenantLoading(true)
    setTenantModalOpen(true)
    try {
      const res = await api.admin.getTenant(tenantId)
      setSelectedTenant(res.tenant)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenant details')
      setTenantModalOpen(false)
    } finally {
      setTenantLoading(false)
    }
  }
  
  const handleEditTenant = (tenant: AdminTenant) => {
    setEditTenantData({ name: tenant.name, slug: tenant.slug })
    setSelectedTenant({ ...tenant } as AdminTenantDetail)
    setEditModalOpen(true)
  }
  
  const handleSaveEdit = async () => {
    if (!selectedTenant || !editTenantData) return
    setEditingSaving(true)
    try {
      await api.admin.updateTenant(selectedTenant.id, editTenantData)
      await loadData()
      setEditModalOpen(false)
      setEditTenantData(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant')
    } finally {
      setEditingSaving(false)
    }
  }
  
  const handleDeleteClick = (tenant: AdminTenant) => {
    setTenantToDelete(tenant)
    setDeleteTypedName('')
    setDeleteConfirmOpen(true)
  }
  
  const handleConfirmDelete = async () => {
    if (!tenantToDelete || deleteTypedName !== tenantToDelete.name) return
    setDeleting(true)
    try {
      await api.admin.deleteTenant(tenantToDelete.id)
      await loadData()
      setDeleteConfirmOpen(false)
      setTenantToDelete(null)
      setDeleteTypedName('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tenant')
    } finally {
      setDeleting(false)
    }
  }
  
  const handleToggleAdmin = async (userId: string, currentStatus: boolean) => {
    try {
      await api.admin.toggleGlobalAdmin(userId, !currentStatus)
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update admin status')
    }
  }
  
  const toggleTenantExpand = (tenantId: string) => {
    const newExpanded = new Set(expandedTenants)
    if (newExpanded.has(tenantId)) {
      newExpanded.delete(tenantId)
    } else {
      newExpanded.add(tenantId)
    }
    setExpandedTenants(newExpanded)
  }
  
  // User management handlers
  const handleEditUser = (u: AdminUser) => {
    setSelectedUser(u)
    setEditUserData({ name: u.name, email: u.email })
    setEditUserModalOpen(true)
  }
  
  const handleSaveUser = async () => {
    if (!selectedUser) return
    setEditUserSaving(true)
    try {
      await api.admin.updateUser(selectedUser.id, editUserData)
      setSuccessMessage('User updated successfully')
      await loadData()
      setEditUserModalOpen(false)
      setSelectedUser(null)
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setEditUserSaving(false)
    }
  }
  
  const handlePasswordReset = async (u: AdminUser) => {
    setResetPasswordLoading(u.id)
    try {
      const res = await api.admin.triggerPasswordReset(u.id)
      setSuccessMessage(res.message)
      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send password reset')
    } finally {
      setResetPasswordLoading(null)
    }
  }
  
  const handleDeleteUserClick = (u: AdminUser) => {
    setUserToDelete(u)
    setDeleteUserTypedName('')
    setDeleteUserConfirmOpen(true)
  }
  
  const handleConfirmDeleteUser = async () => {
    if (!userToDelete || deleteUserTypedName !== userToDelete.name) return
    setDeletingUser(true)
    try {
      await api.admin.deleteUser(userToDelete.id)
      setSuccessMessage('User deleted successfully')
      await loadData()
      setDeleteUserConfirmOpen(false)
      setUserToDelete(null)
      setDeleteUserTypedName('')
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    } finally {
      setDeletingUser(false)
    }
  }

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }
  
  if (!user.isGlobalAdmin) {
    return null // Will redirect
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-gray-400 hover:text-gray-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-primary-600" />
                <h1 className="text-xl font-semibold text-gray-900">Global Admin</h1>
              </div>
            </div>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('tenants')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'tenants'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Tenants ({tenants.length})
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'users'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              People ({users.length})
            </button>
            <Link
              to="/admin/stripe"
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-1.5 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300`}
            >
              <CreditCard className="w-4 h-4" />
              Payments
            </Link>
          </nav>
        </div>
      </div>
      
      {/* Error Banner */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
            <p className="text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && stats && (
              <div className="space-y-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                  <StatCard
                    icon={Users}
                    label="Total Users"
                    value={stats.totalUsers}
                    color="blue"
                  />
                  <StatCard
                    icon={Building2}
                    label="Organizations"
                    value={stats.totalOrgs}
                    color="purple"
                  />
                  <StatCard
                    icon={Users}
                    label="Memberships"
                    value={stats.activeMemberships}
                    color="indigo"
                  />
                  <StatCard
                    icon={Building2}
                    label="Buildings"
                    value={stats.totalBuildings}
                    color="teal"
                  />
                  <StatCard
                    icon={DoorOpen}
                    label="Rooms"
                    value={stats.totalRooms}
                    color="green"
                  />
                  <StatCard
                    icon={Calendar}
                    label="Total Bookings"
                    value={stats.totalBookings}
                    color="orange"
                  />
                  <StatCard
                    icon={TrendingUp}
                    label="Today"
                    value={stats.bookingsToday}
                    color="pink"
                  />
                </div>
                
                {/* Bookings Chart */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Bookings (Last 30 Days)</h3>
                  {bookingsByDay.length > 0 ? (
                    <div className="h-48">
                      <SimpleBarChart data={bookingsByDay} />
                    </div>
                  ) : (
                    <p className="text-gray-500">No booking data available</p>
                  )}
                </div>
                
                {/* Quick Stats Tables */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Top Tenants by Bookings */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Tenants by Bookings</h3>
                    <div className="space-y-3">
                      {tenants
                        .sort((a, b) => b.bookingCount - a.bookingCount)
                        .slice(0, 5)
                        .map((tenant, i) => (
                          <div key={tenant.id} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-400 w-4">{i + 1}</span>
                              <span className="text-sm font-medium text-gray-900">{tenant.name}</span>
                            </div>
                            <span className="text-sm text-gray-600">{tenant.bookingCount} bookings</span>
                          </div>
                        ))}
                    </div>
                  </div>
                  
                  {/* Recent Users */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Users</h3>
                    <div className="space-y-3">
                      {users
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                        .slice(0, 5)
                        .map((u) => (
                          <div key={u.id} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium text-sm">
                                {u.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900">{u.name}</span>
                                <p className="text-xs text-gray-500">{u.email}</p>
                              </div>
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(u.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Tenants Tab */}
            {activeTab === 'tenants' && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Organization
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Slug
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Members
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Buildings
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rooms
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bookings
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tenants.map((tenant) => (
                      <>
                        <tr key={tenant.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => toggleTenantExpand(tenant.id)}
                              className="flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-primary-600"
                            >
                              {expandedTenants.has(tenant.id) ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                              {tenant.name}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {tenant.slug}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            {tenant.memberCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            {tenant.buildingCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            {tenant.roomCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            {tenant.bookingCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(tenant.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleViewTenant(tenant.id)}
                                className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
                                title="View details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEditTenant(tenant)}
                                className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(tenant)}
                                className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedTenants.has(tenant.id) && (
                          <tr key={`${tenant.id}-details`}>
                            <td colSpan={8} className="px-6 py-4 bg-gray-50">
                              <div className="text-sm text-gray-600">
                                <p><strong>Settings:</strong> {JSON.stringify(tenant.settings)}</p>
                                <p><strong>Last Updated:</strong> {new Date(tenant.updatedAt).toLocaleString()}</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* People Tab */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, email, or organisation..."
                    value={peopleSearch}
                    onChange={(e) => setPeopleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {peopleSearch && (
                    <button
                      onClick={() => setPeopleSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Person
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Memberships
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Global Admin
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users
                      .filter((u) => {
                        if (!peopleSearch.trim()) return true
                        const search = peopleSearch.toLowerCase()
                        return (
                          u.name.toLowerCase().includes(search) ||
                          u.email.toLowerCase().includes(search) ||
                          u.memberships.some(m => 
                            m.orgName.toLowerCase().includes(search) ||
                            m.orgSlug.toLowerCase().includes(search)
                          )
                        )
                      })
                      .map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium text-sm">
                              {u.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-medium text-gray-900">{u.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {u.email}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {u.memberships.slice(0, 3).map((m) => (
                              <span
                                key={m.orgId}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {m.orgName} ({m.role})
                              </span>
                            ))}
                            {u.memberships.length > 3 && (
                              <span className="text-xs text-gray-500">
                                +{u.memberships.length - 3} more
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(u.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => handleToggleAdmin(u.id, u.isGlobalAdmin)}
                            disabled={u.id === user?.id}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                              u.isGlobalAdmin
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            } ${u.id === user?.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={u.id === user?.id ? "You cannot change your own admin status" : ""}
                          >
                            {u.isGlobalAdmin ? (
                              <>
                                <CheckCircle className="w-3.5 h-3.5" />
                                Admin
                              </>
                            ) : (
                              'Make Admin'
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleEditUser(u)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              title="Edit user"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handlePasswordReset(u)}
                              disabled={resetPasswordLoading === u.id}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                              title="Send password reset"
                            >
                              {resetPasswordLoading === u.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Key className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDeleteUserClick(u)}
                              disabled={u.id === user?.id}
                              className={`p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors ${
                                u.id === user?.id ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              title={u.id === user?.id ? "Cannot delete yourself" : "Delete user"}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Tenant Detail Modal */}
      {tenantModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setTenantModalOpen(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedTenant?.name || 'Loading...'}
                </h3>
                <button
                  onClick={() => setTenantModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[calc(80vh-4rem)]">
                {tenantLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : selectedTenant ? (
                  <div className="space-y-6">
                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900">{selectedTenant.memberCount}</p>
                        <p className="text-sm text-gray-500">Members</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900">{selectedTenant.buildingCount}</p>
                        <p className="text-sm text-gray-500">Buildings</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900">{selectedTenant.roomCount}</p>
                        <p className="text-sm text-gray-500">Rooms</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900">{selectedTenant.bookingCount}</p>
                        <p className="text-sm text-gray-500">Bookings</p>
                      </div>
                    </div>
                    
                    {/* Members */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Members</h4>
                      <div className="bg-gray-50 rounded-lg overflow-hidden">
                        <table className="min-w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Role</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {selectedTenant.members.map((m) => (
                              <tr key={m.id}>
                                <td className="px-4 py-2 text-sm text-gray-900">{m.name}</td>
                                <td className="px-4 py-2 text-sm text-gray-500">{m.email}</td>
                                <td className="px-4 py-2 text-sm text-gray-500 capitalize">{m.role}</td>
                                <td className="px-4 py-2">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    m.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {m.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    {/* Buildings */}
                    {selectedTenant.buildings.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Buildings</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedTenant.buildings.map((b) => (
                            <span key={b.id} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                              <Building2 className="w-3.5 h-3.5 mr-1.5" />
                              {b.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Rooms */}
                    {selectedTenant.rooms.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Rooms</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedTenant.rooms.map((r) => (
                            <span key={r.id} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                              r.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                            }`}>
                              <DoorOpen className="w-3.5 h-3.5 mr-1.5" />
                              {r.name}
                              {r.isPublic && <span className="ml-1 text-xs">(public)</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Tenant Modal */}
      {editModalOpen && editTenantData && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setEditModalOpen(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Edit Tenant</h3>
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editTenantData.name}
                    onChange={(e) => setEditTenantData({ ...editTenantData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                  <input
                    type="text"
                    value={editTenantData.slug}
                    onChange={(e) => setEditTenantData({ ...editTenantData, slug: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={editingSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {editingSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && tenantToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setDeleteConfirmOpen(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Tenant</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  This will permanently delete <strong>{tenantToDelete.name}</strong> and all its data including:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 mb-4 space-y-1">
                  <li>{tenantToDelete.memberCount} member(s)</li>
                  <li>{tenantToDelete.buildingCount} building(s)</li>
                  <li>{tenantToDelete.roomCount} room(s)</li>
                  <li>{tenantToDelete.bookingCount} booking(s)</li>
                </ul>
                <p className="text-sm text-gray-600 mb-4">
                  Type <strong>{tenantToDelete.name}</strong> to confirm:
                </p>
                <input
                  type="text"
                  value={deleteTypedName}
                  onChange={(e) => setDeleteTypedName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
                  placeholder="Type tenant name..."
                />
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setDeleteConfirmOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deleting || deleteTypedName !== tenantToDelete.name}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleting ? 'Deleting...' : 'Delete Tenant'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Success Message Banner */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 shadow-lg">
          <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
          <p className="text-green-700">{successMessage}</p>
          <button
            onClick={() => setSuccessMessage(null)}
            className="ml-2 text-green-500 hover:text-green-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Edit User Modal */}
      {editUserModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setEditUserModalOpen(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Edit User</h3>
                <button
                  onClick={() => setEditUserModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editUserData.name}
                    onChange={(e) => setEditUserData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editUserData.email}
                    onChange={(e) => setEditUserData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
                <button
                  onClick={() => setEditUserModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={editUserSaving || !editUserData.name || !editUserData.email}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {editUserSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete User Confirmation Modal */}
      {deleteUserConfirmOpen && userToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setDeleteUserConfirmOpen(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-red-600 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Delete User Account
                </h3>
                <button
                  onClick={() => setDeleteUserConfirmOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <p className="text-gray-700">
                  You are about to permanently delete the account for <strong>{userToDelete.name}</strong> ({userToDelete.email}).
                </p>
                <p className="text-gray-700">
                  This will remove all their memberships, notifications, and account data. This action cannot be undone.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type "{userToDelete.name}" to confirm:
                  </label>
                  <input
                    type="text"
                    value={deleteUserTypedName}
                    onChange={(e) => setDeleteUserTypedName(e.target.value)}
                    placeholder={userToDelete.name}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
                <button
                  onClick={() => setDeleteUserConfirmOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeleteUser}
                  disabled={deletingUser || deleteUserTypedName !== userToDelete.name}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingUser ? 'Deleting...' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Stat Card Component
function StatCard({ 
  icon: Icon, 
  label, 
  value, 
  color 
}: { 
  icon: React.ElementType
  label: string
  value: number
  color: 'blue' | 'purple' | 'indigo' | 'teal' | 'green' | 'orange' | 'pink'
}) {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-teal-100 text-teal-600',
    indigo: 'bg-primary-100 text-primary-600',
    teal: 'bg-teal-100 text-teal-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    pink: 'bg-pink-100 text-pink-600',
  }
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  )
}

// Simple Bar Chart Component
function SimpleBarChart({ data }: { data: { date: string; count: number }[] }) {
  const maxCount = Math.max(...data.map(d => d.count), 1)
  
  return (
    <div className="flex items-end justify-between h-full gap-1">
      {data.map((d, i) => {
        const height = (d.count / maxCount) * 100
        const date = new Date(d.date)
        const isWeekend = date.getDay() === 0 || date.getDay() === 6
        
        return (
          <div key={i} className="flex-1 flex flex-col items-center h-full">
            <div className="flex-1 w-full flex items-end">
              <div
                className={`w-full rounded-t ${isWeekend ? 'bg-gray-300' : 'bg-primary-500'}`}
                style={{ height: `${Math.max(height, 2)}%` }}
                title={`${d.date}: ${d.count} bookings`}
              />
            </div>
            {i % 5 === 0 && (
              <span className="text-[10px] text-gray-400 mt-1">
                {date.getDate()}/{date.getMonth() + 1}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}
